import { z } from "zod";
import { ProjectReviewSettingsSchema, ProjectReviewStatusSchema, SubmitProjectReviewSchema, type SubmitProjectReviewBody, type UpdateProjectReviewSettingsBody } from "../schemas/reviews-v2";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import type { UploadProjectAttachmentsParams } from "../schemas/projects-v2";
import {
    GenericProjectListResponseItemSchema,
    ProjectAttachmentResponseSchema,
    ProjectConfigResponseSchema,
    ProjectDetailResponseSchema,
    ProjectParticipantsDisplayResponseSchema,
    ProjectQuoteCheckoutSessionResponseSchema,
    ProjectQuoteListItemSchema,
    ProjectQuoteResponseSchema,
    type UpdateProjectStatusRequest,
    UpdateProjectStatusResponseSchema,
    type CreateProjectQuoteRequest,
    type UpdateProjectQuoteRequest,
    type ProjectActingOrgRequest,
    type UpdateProjectQuotePaymentStatusRequest,
    type ProjectsV2ListQueryParams,
} from "../schemas/projects-v2";

function buildAttachmentsFormData(params: UploadProjectAttachmentsParams): FormData {
    const formData = new FormData();
    formData.append("acting_org_id", String(params.acting_org_id));
    params.files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
    });

    if (params.metadata && Object.keys(params.metadata).length > 0) {
        formData.append("metadata", JSON.stringify(params.metadata));
    }

    return formData;
}

export function createProjectsV2CommonApi(http: HttpClient, baseUrl: string) {
    const projectsBaseUrl = `${baseUrl}/projects`;
    const quotesListSchema = z.array(ProjectQuoteListItemSchema);
    const attachmentsListSchema = z.array(ProjectAttachmentResponseSchema);

    const uploadAttachmentsEndpoint = createEndpoint<
        z.infer<typeof attachmentsListSchema>,
        FormData,
        QueryParams,
        { projectId: number }
    >(http, "POST", "/:projectId/attachments", {
        base: projectsBaseUrl,
        asFormData: true,
        responseSchema: attachmentsListSchema,
        params: ["projectId"],
    });

    const submitReview = createEndpoint<z.infer<typeof ProjectReviewStatusSchema>, SubmitProjectReviewBody, QueryParams, { projectId: number }>(http, "POST", "/:projectId/review", {
        base: projectsBaseUrl,
        responseSchema: ProjectReviewStatusSchema,
        params: ["projectId"],
    });

    return {
        getReview: createEndpoint<z.infer<typeof ProjectReviewStatusSchema>, unknown, QueryParams, { projectId: number }>(http, "GET", "/:projectId/review", {
            base: projectsBaseUrl,
            responseSchema: ProjectReviewStatusSchema,
            params: ["projectId"],
        }),
        submitReview(args: { params: { projectId: number }; body: SubmitProjectReviewBody }) {
            return submitReview({ ...args, body: SubmitProjectReviewSchema.parse(args.body) });
        },
        /**
         * Turn a project's automatic review requests on or off (expert only).
         * PATCH /api/v2/projects/:projectId/review-settings
         */
        updateReviewSettings: createEndpoint<z.infer<typeof ProjectReviewSettingsSchema>, UpdateProjectReviewSettingsBody, QueryParams, { projectId: number }>(http, "PATCH", "/:projectId/review-settings", {
            base: projectsBaseUrl,
            responseSchema: ProjectReviewSettingsSchema,
            params: ["projectId"],
        }),
        /**
         * Get V2 project domain configuration.
         * GET /api/v2/projects/config
         */
        getConfig: createEndpoint<
            z.infer<typeof ProjectConfigResponseSchema>,
            unknown,
            QueryParams,
            Record<string, never>
        >(http, "GET", "/config", {
            base: projectsBaseUrl,
            responseSchema: ProjectConfigResponseSchema,
        }),

        /**
         * Generic org-aware project list.
         * GET /api/v2/projects
         */
        listProjects: createEndpointWithPagination<
            z.infer<typeof GenericProjectListResponseItemSchema>,
            unknown,
            unknown,
            ProjectsV2ListQueryParams
        >(http, "GET", "", {
            base: projectsBaseUrl,
            itemSchema: GenericProjectListResponseItemSchema,
        }),

        /**
         * Get a single V2 project by project id.
         * GET /api/v2/projects/:projectId
         */
        getProject: createEndpoint<
            z.infer<typeof ProjectDetailResponseSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "GET", "/:projectId", {
            base: projectsBaseUrl,
            responseSchema: ProjectDetailResponseSchema,
            params: ["projectId"],
        }),

        /**
         * Get the participant display overlay for chat rendering.
         * GET /api/v2/projects/:projectId/participants/display
         */
        getParticipantsDisplay: createEndpoint<
            z.infer<typeof ProjectParticipantsDisplayResponseSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "GET", "/:projectId/participants/display", {
            base: projectsBaseUrl,
            responseSchema: ProjectParticipantsDisplayResponseSchema,
            params: ["projectId"],
        }),

        /**
         * Update a project's lifecycle status.
         * PUT /api/v2/projects/:projectId/status
         */
        updateStatus: createEndpoint<
            z.infer<typeof UpdateProjectStatusResponseSchema>,
            UpdateProjectStatusRequest,
            QueryParams,
            { projectId: number }
        >(http, "PUT", "/:projectId/status", {
            base: projectsBaseUrl,
            responseSchema: UpdateProjectStatusResponseSchema,
            params: ["projectId"],
        }),

        /**
         * List quotes for a project.
         * GET /api/v2/projects/:projectId/quotes
         */
        listQuotes: createEndpoint<
            z.infer<typeof quotesListSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "GET", "/:projectId/quotes", {
            base: projectsBaseUrl,
            responseSchema: quotesListSchema,
            params: ["projectId"],
        }),

        /**
         * Create a quote for a project.
         * POST /api/v2/projects/:projectId/quotes
         */
        createQuote: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            CreateProjectQuoteRequest,
            QueryParams,
            { projectId: number }
        >(http, "POST", "/:projectId/quotes", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId"],
        }),

        /**
         * Create a fresh Stripe Checkout Session for an internal quote.
         * POST /api/v2/quotes/:quoteId/checkout-session
         */
        createQuoteCheckoutSession: createEndpoint<
            z.infer<typeof ProjectQuoteCheckoutSessionResponseSchema>,
            unknown,
            QueryParams,
            { quoteId: number }
        >(http, "POST", "/quotes/:quoteId/checkout-session", {
            base: baseUrl,
            responseSchema: ProjectQuoteCheckoutSessionResponseSchema,
            params: ["quoteId"],
        }),

        /**
         * Update an offered quote.
         * PUT /api/v2/projects/:projectId/quotes/:quoteId
         */
        updateQuote: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            UpdateProjectQuoteRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "PUT", "/:projectId/quotes/:quoteId", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        /**
         * Cancel a quote.
         * POST /api/v2/projects/:projectId/quotes/:quoteId/cancel
         */
        cancelQuote: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            ProjectActingOrgRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "POST", "/:projectId/quotes/:quoteId/cancel", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        /**
         * Reject a quote.
         * POST /api/v2/projects/:projectId/quotes/:quoteId/reject
         */
        rejectQuote: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            ProjectActingOrgRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "POST", "/:projectId/quotes/:quoteId/reject", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        /**
         * Mark a quote as viewed by the client.
         * POST /api/v2/projects/:projectId/quotes/:quoteId/view
         */
        viewQuote: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            ProjectActingOrgRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "POST", "/:projectId/quotes/:quoteId/view", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        /**
         * Update quote payment status.
         * PUT /api/v2/projects/:projectId/quotes/:quoteId/payment-status
         */
        updateQuotePaymentStatus: createEndpoint<
            z.infer<typeof ProjectQuoteResponseSchema>,
            UpdateProjectQuotePaymentStatusRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "PUT", "/:projectId/quotes/:quoteId/payment-status", {
            base: projectsBaseUrl,
            responseSchema: ProjectQuoteResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        /**
         * List permanent project attachments.
         * GET /api/v2/projects/:projectId/attachments
         */
        listAttachments: createEndpoint<
            z.infer<typeof attachmentsListSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "GET", "/:projectId/attachments", {
            base: projectsBaseUrl,
            responseSchema: attachmentsListSchema,
            params: ["projectId"],
        }),

        /**
         * Upload temporary project attachments.
         * POST /api/v2/projects/:projectId/attachments
         */
        uploadAttachments: async (args: {
            params: { projectId: number };
            body: UploadProjectAttachmentsParams;
            options?: { headers?: Record<string, string>; signal?: AbortSignal };
        }) => {
            const formData = buildAttachmentsFormData(args.body);
            return uploadAttachmentsEndpoint({
                params: args.params,
                body: formData,
                options: args.options,
            });
        },
    };
}

export type ProjectsV2CommonApi = ReturnType<typeof createProjectsV2CommonApi>;
